import { Finding } from '../types/finding';

/**
 * Estimated remediation effort for a finding.
 *
 * The estimate is a lookup table keyed on *what kind of issue it is*, not how severe it
 * is — a critical and a low XSS are the same edit, and severity gets re-graded by the AI
 * assessment while the work does not change. This is the model the market uses (Aikido
 * publish a per-rule constant: SQL injection ~30 min, secrets ~10 min, dependency minor
 * bump 5–15 min, major upgrade 1 hr+), which makes the numbers comparable to what teams
 * already benchmark against.
 *
 * Three deliberate departures, each using data that a per-rule constant ignores:
 *  - dependency findings read the real semver delta between installed and fixed version,
 *    rather than one flat number for all of SCA
 *  - a finding with a fix already generated costs review time, not remediation time
 *  - severity is only the fallback, for findings we could not classify (`ai.other`, or a
 *    rule with no CWE metadata)
 *
 * Counts are not multiplied in: findings are fingerprinted per site (rule + file +
 * enclosing symbol), so one finding is one edit.
 */

/**
 * Effort tiers, in minutes, by the shape of the change the fix requires.
 * Anchored on the published SQLi ≈ 30 min / secrets ≈ 10 min pair.
 */
const EFFORT = {
  /** Swap a constant, flip a flag, rotate a key, add a header. */
  mechanical: 10,
  /** Encode, sanitise or validate at a single call site. */
  localised: 20,
  /** Rework how a query, command or request is built. */
  callPath: 30,
  /** Auth, sessions, concurrency — the change spans the design, not one line. */
  crossCutting: 60,
} as const;

/**
 * CWE → effort tier. Covers the CWEs our scanners emit: the deep AI scan writes the CWE
 * straight into `check_id` and `cwe`, Semgrep carries rule-metadata CWEs, and the secrets
 * scan tags CWE-798.
 */
const CWE_EFFORT: Record<string, number> = {
  // Secrets, crypto choices and switches — replace a value or a call.
  'CWE-798': EFFORT.mechanical, // hardcoded credentials
  'CWE-259': EFFORT.mechanical, // hardcoded password
  'CWE-321': EFFORT.mechanical, // hardcoded cryptographic key
  'CWE-326': EFFORT.mechanical, // inadequate encryption strength
  'CWE-327': EFFORT.mechanical, // broken/risky crypto algorithm
  'CWE-328': EFFORT.mechanical, // weak hash
  'CWE-330': EFFORT.mechanical, // insufficiently random values
  'CWE-338': EFFORT.mechanical, // weak PRNG
  'CWE-489': EFFORT.mechanical, // debug/test code left enabled
  'CWE-209': EFFORT.mechanical, // error message with sensitive info
  'CWE-532': EFFORT.mechanical, // sensitive info in logs
  'CWE-117': EFFORT.mechanical, // log injection
  'CWE-693': EFFORT.mechanical, // missing security headers
  'CWE-942': EFFORT.mechanical, // permissive CORS
  'CWE-1104': EFFORT.mechanical, // unmaintained third-party component
  'CWE-601': EFFORT.mechanical, // open redirect

  // Sanitise / validate / narrow the payload at one site.
  'CWE-20': EFFORT.localised, // improper input validation
  'CWE-22': EFFORT.localised, // path traversal
  'CWE-79': EFFORT.localised, // XSS
  'CWE-113': EFFORT.localised, // header injection
  'CWE-200': EFFORT.localised, // exposure of sensitive information
  'CWE-311': EFFORT.localised, // missing encryption of sensitive data
  'CWE-359': EFFORT.localised, // private information exposure
  'CWE-434': EFFORT.localised, // unrestricted file upload
  'CWE-611': EFFORT.localised, // XXE
  'CWE-915': EFFORT.localised, // mass assignment
  'CWE-1188': EFFORT.localised, // insecure default
  'CWE-16': EFFORT.localised, // configuration

  // Rebuild how the query / command / request is constructed.
  'CWE-78': EFFORT.callPath, // OS command injection
  'CWE-89': EFFORT.callPath, // SQL injection
  'CWE-90': EFFORT.callPath, // LDAP injection
  'CWE-94': EFFORT.callPath, // code injection
  'CWE-98': EFFORT.callPath, // file inclusion
  'CWE-470': EFFORT.callPath, // unsafe reflection
  'CWE-502': EFFORT.callPath, // insecure deserialization
  'CWE-643': EFFORT.callPath, // XPath injection
  'CWE-918': EFFORT.callPath, // SSRF
  'CWE-943': EFFORT.callPath, // NoSQL injection
  'CWE-1336': EFFORT.callPath, // template injection

  // Authorisation, session and concurrency work that spans call sites.
  'CWE-269': EFFORT.crossCutting, // privilege management
  'CWE-284': EFFORT.crossCutting, // improper access control
  'CWE-285': EFFORT.crossCutting, // improper authorization
  'CWE-287': EFFORT.crossCutting, // improper authentication
  'CWE-306': EFFORT.crossCutting, // missing authentication
  'CWE-352': EFFORT.crossCutting, // CSRF
  'CWE-362': EFFORT.crossCutting, // race condition
  'CWE-384': EFFORT.crossCutting, // session fixation
  'CWE-400': EFFORT.crossCutting, // uncontrolled resource consumption
  'CWE-404': EFFORT.crossCutting, // improper resource shutdown
  'CWE-521': EFFORT.crossCutting, // weak password requirements
  'CWE-639': EFFORT.crossCutting, // authorization bypass via user-controlled key
};

/** A patch or minor dependency bump — semver promises it drops in. */
const DEPENDENCY_BUMP_MINUTES = 10;
/** A major upgrade carries breaking changes, so it is scoped work. */
const DEPENDENCY_MAJOR_MINUTES = 60;
/** License policy findings are a swap-or-exception decision, not a code change. */
const LICENSE_POLICY_MINUTES = 15;
/** A fix that already exists only costs a diff review. */
const REVIEW_GENERATED_FIX_MINUTES = 5;

/** Used when the finding carries no CWE we recognise (e.g. `ai.other`). */
const SEVERITY_FALLBACK_MINUTES: Record<string, number> = {
  critical: 45,
  high: 30,
  medium: 15,
  low: 10,
};
const UNKNOWN_MINUTES = 15;

/** Pull `CWE-89` out of `"CWE-89"`, `"cwe-89: SQL Injection"`, etc. */
export const normalizeCwe = (value: string): string | null => {
  const match = /CWE-(\d+)/i.exec(value);
  return match ? `CWE-${match[1]}` : null;
};

/** The installed version from a purl: `pkg:npm/%40scope/name@0.8.12` → `0.8.12`. */
export const parsePurlVersion = (purl: string): string | null => {
  const withoutQualifiers = purl.split(/[?#]/)[0];
  const at = withoutQualifiers.lastIndexOf('@');
  // A leading `@` belongs to an npm scope, not a version.
  if (at <= 0) return null;
  const version = withoutQualifiers.slice(at + 1);
  return version || null;
};

/**
 * Fixed versions the SBOM scan named in the finding message — it writes either
 * "Upgrade pkg to 1.2.3 or later." or "…one of the fixed versions: 1.2.3, 2.0.0.".
 * Kept tolerant: no match just means we fall back to treating it as a bump.
 */
export const parseFixedVersions = (message: string): string[] => {
  const single = /Upgrade\s+\S+\s+to\s+([^\s,]+)\s+or later/i.exec(message);
  if (single) return [single[1].replace(/[.,]$/, '')];

  // Versions contain dots, so run to a period that ends the sentence (or the string).
  const list = /fixed versions:\s*(.+?)\.(?:\s|$)/i.exec(message);
  if (list) {
    return list[1]
      .split(',')
      .map((version) => version.trim().replace(/[.,]$/, ''))
      .filter(Boolean);
  }
  return [];
};

/** Leading numeric component of a version, ignoring a `v` prefix. `v2.1.0` → 2. */
export const majorVersion = (version: string): number | null => {
  const match = /^\D*(\d+)/.exec(version.trim());
  return match ? Number(match[1]) : null;
};

/** Dependency upgrades: major bumps are scoped work, patch/minor drop in. */
const dependencyMinutes = (finding: Pick<Finding, 'file_path' | 'message'>): number => {
  const installed = parsePurlVersion(finding.file_path);
  const installedMajor = installed ? majorVersion(installed) : null;
  if (installedMajor === null) return DEPENDENCY_BUMP_MINUTES;

  const fixedMajors = parseFixedVersions(finding.message)
    .map(majorVersion)
    .filter((major): major is number => major !== null);
  if (fixedMajors.length === 0) return DEPENDENCY_BUMP_MINUTES;

  // The cheapest available upgrade is the one a team would take.
  const nearestMajor = Math.min(...fixedMajors);
  return nearestMajor > installedMajor ? DEPENDENCY_MAJOR_MINUTES : DEPENDENCY_BUMP_MINUTES;
};

type EstimatableFinding = Pick<Finding, 'severity' | 'check_id' | 'file_path' | 'message'> &
  Partial<Pick<Finding, 'status' | 'cwe' | 'category' | 'fix_summary'>>;

/** Effort for the class of issue, before any per-finding adjustment. */
const baseMinutes = (finding: EstimatableFinding): number => {
  if (finding.check_id.startsWith('sbom.license.') || finding.category === 'license') {
    return LICENSE_POLICY_MINUTES;
  }
  if (finding.check_id.startsWith('sbom.vuln.') || finding.category === 'dependency') {
    return dependencyMinutes(finding);
  }

  // The deep AI scan writes the CWE into check_id; Semgrep and secrets scans use `cwe`.
  const candidates = [finding.check_id, ...(finding.cwe ?? [])];
  for (const candidate of candidates) {
    const cwe = normalizeCwe(candidate);
    if (cwe && CWE_EFFORT[cwe] !== undefined) return CWE_EFFORT[cwe];
  }

  return SEVERITY_FALLBACK_MINUTES[finding.severity] ?? UNKNOWN_MINUTES;
};

export function estimateFixMinutes(finding: EstimatableFinding): number {
  // Kolega already wrote the patch — what is left is reading a diff.
  const fixStatus = finding.fix_summary?.fix_status;
  if (fixStatus === 'completed' || finding.fix_summary?.has_pr) return REVIEW_GENERATED_FIX_MINUTES;

  // Unconfirmed findings need triage before the edit itself.
  return finding.status === 'needs_manual_review' ? baseMinutes(finding) * 2 : baseMinutes(finding);
}

/** Format an effort estimate as "~45 min" / "~1 hr" / "~1.5 hrs". */
export function formatFixMinutes(minutes: number): string {
  if (minutes < 60) return `~${minutes} min`;
  const hours = minutes / 60;
  const value = Number.isInteger(hours) ? `${hours}` : hours.toFixed(1);
  return `~${value} ${hours === 1 ? 'hr' : 'hrs'}`;
}

/** The estimated time to fix a finding, ready for display (e.g. "~30 min"). */
export function getFindingFixTime(finding: EstimatableFinding): string {
  return formatFixMinutes(estimateFixMinutes(finding));
}
